/**
 * FighterSvg.jsx
 * 
 * Phase 2: Fighter SVG component that renders characters as DOM elements
 * instead of canvas rectangles. Integrates with existing character SVGs.
 */

import React, { useRef, useEffect, useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { FlameKnight, ShadowNinja, CyberMech, ForestDruid, StormMonk } from '../characters';

// Character component mapping
const characterComponents = {
  flameknight: FlameKnight,
  shadowninja: ShadowNinja,
  cybermech: CyberMech,
  forestdruid: ForestDruid,
  stormmonk: StormMonk,
};

// Default fallback character (FlameKnight) if not found
const DefaultCharacter = FlameKnight;

export const FighterSvg = forwardRef(({
  fighter,
  characterId,
  isLocal = false,
  onAnimationComplete,
}, ref) => {
  const elementRef = useRef(null);
  const [currentState, setCurrentState] = useState('idle');
  const [isFacingLeft, setIsFacingLeft] = useState(false);
  const [isHit, setIsHit] = useState(false);
  const hitTimeoutRef = useRef(null);
  
  // Get character component based on ID
  const CharacterComponent = useMemo(() => {
    // Convert camelCase archetype (e.g., 'flameKnight') to lowercase ('flameknight')
    const id = (characterId || 'flameKnight').toLowerCase();
    return characterComponents[id] || DefaultCharacter;
  }, [characterId]);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    element: elementRef.current,
    
    /**
     * Update fighter position - called from RAF loop at 60fps
     */
    setPosition: (x, y) => {
      if (elementRef.current) {
        elementRef.current.style.left = `${x}px`;
        elementRef.current.style.top = `${y}px`;
      }
    },

    /**
     * Update fighter state
     */
    setState: (state) => {
      setCurrentState(state);
    },

    /**
     * Set facing direction
     */
    setFacing: (facingLeft) => {
      setIsFacingLeft(facingLeft);
    },

    /**
     * Trigger hit reaction (white flash + flinch)
     */
    triggerHit: () => {
      setIsHit(true);
      
      // Clear existing timeout
      if (hitTimeoutRef.current) {
        clearTimeout(hitTimeoutRef.current);
      }
      
      // Reset hit state after flinch duration
      hitTimeoutRef.current = setTimeout(() => {
        setIsHit(false);
      }, 150);
    },

    /**
     * Get current state
     */
    getState: () => currentState,
  }));

  // Sync with fighter data changes
  // Note: fighter is mutated directly by the game engine, so we poll for changes
  useEffect(() => {
    if (!fighter) return;

    let animationFrameId;
    let lastPose = 'idle';
    
    const updatePose = () => {
      // Update position
      if (elementRef.current) {
        elementRef.current.style.left = `${fighter.x}px`;
        elementRef.current.style.top = `${fighter.y}px`;
      }

      // Update facing direction
      setIsFacingLeft(fighter.facing === -1);

      // Determine pose from fighter properties
      // Map game states to character animation poses: idle, attack, jump, hurt, block
      let newPose = 'idle';
      
      if (fighter.hitstun > 0) {
        // Being hit/hurt
        newPose = 'hurt';
      } else if (fighter.attackCooldown > 0.1) {
        // Recently attacked (jab=0.28, special=0.62)
        newPose = 'attack';
      } else if (!fighter.onGround) {
        // In the air (jumping or falling)
        newPose = 'jump';
      }
      // Note: 'run' state uses 'idle' pose (characters don't have a separate run animation)
      // Note: 'block' pose not yet implemented in engine (no shielding mechanic)
      
      // Only update state if pose changed to avoid unnecessary re-renders
      if (newPose !== lastPose) {
        lastPose = newPose;
        setCurrentState(newPose);
      }

      animationFrameId = requestAnimationFrame(updatePose);
    };
    
    animationFrameId = requestAnimationFrame(updatePose);
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [fighter]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (hitTimeoutRef.current) {
        clearTimeout(hitTimeoutRef.current);
      }
    };
  }, []);

  if (!fighter) return null;

  // Calculate visual properties
  const visualWidth = fighter.width || 48;
  const visualHeight = fighter.height || 64;
  
  // Color based on player (local vs remote)
  const playerColor = isLocal ? '#4ade80' : '#f87171'; // Green for local, red for remote

  return (
    <div
      ref={elementRef}
      className={`
        fighter-element
        ${currentState}
        ${isFacingLeft ? 'facing-left' : ''}
        ${isHit ? 'is-hit' : ''}
        ${fighter.invulnerable > 0 ? 'is-invulnerable' : ''}
        ${fighter.shielding ? 'is-shielding' : ''}
      `}
      style={{
        position: 'absolute',
        left: fighter.x,
        top: fighter.y,
        width: visualWidth,
        height: visualHeight,
      }}
      data-state={currentState}
      data-facing={isFacingLeft ? 'left' : 'right'}
      data-player={isLocal ? 'local' : 'remote'}
    >
      {/* Character SVG (flipped when facing left) */}
      <div 
        className="fighter-svg-container"
        style={{
          width: '100%',
          height: '100%',
          transform: isFacingLeft ? 'scaleX(-1)' : 'none',
          transformOrigin: 'center center',
        }}
      >
        <CharacterComponent
          pose={currentState}
        />
      </div>

      {/* Damage percentage (floating above fighter - NOT flipped) */}
      <div 
        className="fighter-damage-indicator"
        style={{
          position: 'absolute',
          top: -25,
          left: '50%',
          transform: `translateX(-50%) ${isFacingLeft ? 'scaleX(-1)' : ''}`,
          fontSize: '14px',
          fontWeight: 'bold',
          color: fighter.hp > 100 ? '#ef4444' : fighter.hp > 50 ? '#f59e0b' : '#ffffff',
          textShadow: '0 1px 2px rgba(0,0,0,0.8)',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {Math.floor(fighter.hp || 0)}%
      </div>

      {/* Player indicator (P1/P2 - NOT flipped) */}
      <div
        className="fighter-player-indicator"
        style={{
          position: 'absolute',
          top: -40,
          left: '50%',
          transform: `translateX(-50%) ${isFacingLeft ? 'scaleX(-1)' : ''}`,
          fontSize: '12px',
          fontWeight: 'bold',
          color: playerColor,
          textShadow: '0 1px 2px rgba(0,0,0,0.8)',
          pointerEvents: 'none',
        }}
      >
        {fighter.slot === 'p1' ? 'P1' : 'P2'}
      </div>
    </div>
  );
});

FighterSvg.displayName = 'FighterSvg';

export default FighterSvg;
